import React, { useMemo, useCallback } from 'react';
import { Box, Paper, IconButton, Button } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { useTheme } from '../../contexts/ThemeContext';
import { getCardSx } from '../../theme/tokens';
import { updateUnitContent, uploadUnitAsset, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm4_u2_x4_regelungsstrategie';

const images = [
  { key: 'placeholder', src: '/assets/drawable/zzz_help.png', label: 'Regelstrategie Schema (Platzhalter)' }
];

const textBlocks = [
    { key: 'block_titel', heading: 'Regelungsstrategie – Luftqualität & Raumkomfort', body: '' },
    { key: 'block_placeholder', heading: 'Platzhalter', body: 'Für diese Unterseite liegen derzeit keine extrahierten Android-Texte vor. Admins können hier Inhalte ergänzen (Messgrößen, Prioritäten, Kombinierte Strategien CO₂/rH/VOC, Sicherheitsgrenzen, Optimierungslogik). Bitte über das ✏️ Overlay bearbeiten.' },
    { key: 'block_ideen', heading: 'Mögliche Strategien (Ideenliste)', body: '• CO₂-geführt: Außenluftanteil dynamisch erhöhen\n• rH-Überwachung: Entfeuchtung/Befeuchtung bedarfsgeführt\n• VOC-Adaption: Lüftungsintensität bei hoher Gesamtbelastung\n• Kombinierte Priorisierung: Sicherheit > Luftqualität > Energieeffizienz\n• Nachtspülung zur Reduktion angereicherter VOC/CO₂\n• Präsenzabhängige Umschaltung von Minimalbetrieb auf Komfortbetrieb\n• Filterzustand einbeziehen (Alarm / Effizienz)\n• Adaptive Grenzwerte (Jahreszeit / Nutzung)' },
    { key: 'block_parameter', heading: 'Typische Parameter', body: '• CO₂-Schwellen (800 / 1000 / 1400 ppm)\n• rH-Grenzen (Untergrenze 35–40%, Obergrenze 60–65%)\n• TVOC-Richtwerte (siehe VOC-Seite)\n• Mindestluftwechsel (h⁻¹)\n• Nachlaufzeiten Lüftung / Einschaltverzögerungen\n• Prioritätenmatrix (Manuell / Automatik / Sicherheit)\n• Zeitprogramme (Tag / Nacht / Wochenenden)' }
];

const M4_U2_X4_Regelungsstrategie: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM4, canUploadImagesM4 } = useAuth();
  const canEditTexts = canEditTextsM4;
  const canUploadImages = canUploadImagesM4;
  const { darkMode } = useTheme();
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const mergedBlocks = useMemo(() => textBlocks.map(tb => ({ ...tb, body: override?.texts?.[tb.key] || tb.body })), [override]);
  const fullText = useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = useCallback(async (blockKey: string, newBody: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try { await updateUnitContent(PAGE_ID, { texts: { [blockKey]: newBody } }, token); alert('Gespeichert.'); await refresh(); } catch (e) { console.error(e); alert('Fehler.'); }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try { await uploadUnitAsset(PAGE_ID, imageKey, file, token); alert('Bild aktualisiert.'); await refresh(); } catch (e) { console.error(e); alert('Fehler.'); }
  }, [token, refresh, canUploadImages]);

  const ttsBtn = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'TTS', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '4.2.4 Regelungsstrategie Luft', actions: ttsBtn },
    React.createElement(Paper, { sx: getCardSx(darkMode), elevation: 2 },
      React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3 } },
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } }, images.map(img => React.createElement(ZoomableImage, { key: img.key, src: imgSrc(img.key, img.src), label: img.label, onReplace: canUploadImages ? (f: File) => handleImageReplace(img.key, f) : undefined })) ),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } }, mergedBlocks.map(tb => React.createElement(EditableTextBlock, { key: tb.key, heading: tb.heading, body: tb.body, onSave: canEditTexts ? (v: string) => handleTextSave(tb.key, v) : undefined, pageId: PAGE_ID, blockId: tb.key })) )
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
      React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m4_u2_x99_quiz') }, 'Zum Quiz')
    )
  );
};

export default M4_U2_X4_Regelungsstrategie;

